import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useDashboardAttributionSafe } from './DashboardAttributionContext';

export type DataSourceType = "mock" | "meta" | "all" | "unified";

interface DashboardDataSource {
  sourceType: DataSourceType;
  sourceId: number;
  accountIds?: number[];
  platformSlugs?: string[];
  brandId?: number | null;
  dataKey: string;
  getUrl: (endpoint: string, params?: URLSearchParams) => string;
}

const DashboardDataSourceContext = createContext<DashboardDataSource | null>(null);

export function isDemoAccount(account: { accountId?: string; connection?: { fbUserId?: string } | null }): boolean {
  if (account.accountId?.startsWith("act_demo_")) return true;
  if (account.connection && "fbUserId" in account.connection && account.connection.fbUserId === "demo") return true;
  return false;
}

export function DashboardDataSourceProvider({
  sourceType,
  sourceId,
  allBrandIds,
  accountIds,
  platformSlugs,
  brandId,
  children,
}: {
  sourceType: DataSourceType;
  sourceId: number;
  allBrandIds?: number[];
  accountIds?: number[];
  platformSlugs?: string[];
  brandId?: number | null;
  children: ReactNode;
}) {
  const brandIdsStr = allBrandIds?.join(",") || "";
  const accountIdsStr = accountIds?.join(",") || "";
  const platformSlugsStr = platformSlugs?.join(",") || "";
  const brandIdStr = brandId ? String(brandId) : "";
  const attribution = useDashboardAttributionSafe()?.attributionWindow || 'account_default';

  const value = useMemo<DashboardDataSource>(() => {
    const getUrl = (endpoint: string, params?: URLSearchParams) => {
      let base: string;
      if (sourceType === "unified") {
        base = `/api/dashboard/data/${endpoint}`;
      } else if (sourceType === "meta") {
        base = `/api/meta/accounts/${sourceId}/dashboard/${endpoint}`;
      } else if (sourceType === "all") {
        base = `/api/mock-insights/all/${endpoint}`;
      } else {
        base = `/api/mock-insights/${sourceId}/${endpoint}`;
      }

      const p = params ? new URLSearchParams(params) : new URLSearchParams();
      if (sourceType === 'unified') p.set('attribution', attribution);
      if (sourceType === "all" && brandIdsStr) {
        p.set("brandIds", brandIdsStr);
      }
      if (sourceType === "unified") {
        if (accountIdsStr) p.set("accounts", accountIdsStr);
        if (platformSlugsStr) p.set("platforms", platformSlugsStr);
        if (brandIdStr) p.set("brand_id", brandIdStr);
      }
      const qs = p.toString();
      return qs ? `${base}?${qs}` : base;
    };

    const dataKey = `${sourceType}:${sourceId}:${accountIdsStr}:${platformSlugsStr}:${brandIdStr}:${attribution}`;
    return { sourceType, sourceId, accountIds, platformSlugs, brandId, dataKey, getUrl };
  }, [sourceType, sourceId, brandIdsStr, accountIdsStr, platformSlugsStr, brandIdStr, attribution]);

  return (
    <DashboardDataSourceContext.Provider value={value}>
      {children}
    </DashboardDataSourceContext.Provider>
  );
}

export function useDashboardDataSource(): DashboardDataSource {
  const ctx = useContext(DashboardDataSourceContext);
  if (!ctx) throw new Error("useDashboardDataSource must be used within DashboardDataSourceProvider");
  return ctx;
}

export function useDashboardDataSourceSafe(): DashboardDataSource | null {
  return useContext(DashboardDataSourceContext);
}
